<!--
	ConversationCollabBar.svelte
	Compact bar of collaboration actions for the currently selected conversation:
	- Primary assignee badge (claim if unassigned)
	- Transfer to user/team
	- AI status indicator (paused/active) + toggle
	Reads `conversation` via $props and emits `onUpdated(conv)` after changes.
-->
<script>
	import { teamAPI, crmAPI } from './api.js';
	import { toast } from './lib/toast.js';
	import Icon from './lib/Icon.svelte';

	let { conversation, onUpdated = () => {} } = $props();

	let busy = $state(false);
	let menuOpen = $state(false);
	let roster = $state([]);
	let teams = $state([]);
	let rosterLoaded = $state(false);
	let creatingLead = $state(false);

	// Lead panel (gestionar lead del contacto desde el chat)
	let lead = $state(null);
	let stages = $state([]);          // stages of the lead's pipeline
	let leadLoadedFor = $state(null); // conversation id we loaded the lead for

	$effect(() => {
		const cid = conversation?.id;
		if (cid && cid !== leadLoadedFor) {
			leadLoadedFor = cid;
			loadLead();
		}
	});

	async function loadLead() {
		lead = null;
		stages = [];
		const contactId = conversation?.contact_id || conversation?.contact?.id;
		if (!contactId) return;
		try {
			const leads = await crmAPI.listLeads(null, null, contactId);
			lead = (leads && leads[0]) || null;
			if (lead) await loadStages(lead.pipeline_id);
		} catch { /* sin lead */ }
	}

	async function loadStages(pipelineId) {
		try {
			const pipelines = await crmAPI.getPipelines();
			const p = (pipelines || []).find(p => p.id === pipelineId);
			stages = p?.stages || [];
		} catch { stages = []; }
	}

	async function moveStage(stageId) {
		if (!lead || !stageId || stageId === lead.stage_id) return;
		busy = true;
		try {
			lead = await crmAPI.moveLead(lead.id, Number(stageId));
			toast.success('Lead movido de etapa');
		} catch (e) { toast.error(e.message || 'No se pudo mover el lead'); }
		finally { busy = false; }
	}

	async function setLeadStatus(status) {
		if (!lead) return;
		busy = true;
		try {
			lead = await crmAPI.updateLead(lead.id, { status });
			toast.success(status === 'won' ? '🏆 Lead marcado como ganado'
				: status === 'lost' ? 'Lead marcado como perdido'
				: 'Lead reabierto');
		} catch (e) { toast.error(e.message || 'No se pudo actualizar el lead'); }
		finally { busy = false; }
	}

	async function ensureRoster() {
		if (rosterLoaded) return;
		try {
			const [r, t] = await Promise.all([
				teamAPI.getPresence(),
				teamAPI.listTeams()
			]);
			roster = r || [];
			teams = t || [];
			rosterLoaded = true;
		} catch (e) {
			toast.error(e.message || 'No se pudo cargar el equipo');
		}
	}

	async function claim() {
		busy = true;
		try {
			const updated = await teamAPI.claim(conversation.id);
			onUpdated(updated);
			toast.success('Conversación reclamada');
		} catch (e) { toast.error(e.message || 'No se pudo reclamar'); }
		finally { busy = false; }
	}

	async function transferTo(opts) {
		busy = true;
		try {
			const result = await teamAPI.transfer(conversation.id, opts);
			menuOpen = false;
			onUpdated({
				...conversation,
				primary_assignee_id: result.to_user?.id ?? conversation.primary_assignee_id,
				primary_assignee: result.to_user || conversation.primary_assignee,
				ai_paused: true
			});
			toast.success(`Transferida a ${result.to_user?.name || 'agente'}`);
		} catch (e) { toast.error(e.message || 'No se pudo transferir'); }
		finally { busy = false; }
	}

	async function toggleAI() {
		busy = true;
		try {
			if (conversation.ai_paused) {
				const updated = await teamAPI.releaseAI(conversation.id);
				onUpdated(updated);
				toast.success('IA reanudada en esta conversación');
			}
		} catch (e) { toast.error(e.message || 'No se pudo cambiar el estado de IA'); }
		finally { busy = false; }
	}

	async function createLead() {
		if (creatingLead) return;
		creatingLead = true;
		try {
			const created = await crmAPI.createLeadFromConversation(conversation.id);
			lead = created;
			if (created?.pipeline_id) await loadStages(created.pipeline_id);
			toast.success('Lead creado', { title: `"${created.title}"` });
		} catch (e) {
			toast.error(e.message || 'No se pudo crear el lead');
		} finally {
			creatingLead = false;
		}
	}
</script>

<div class="collab-bar">
	{#if conversation.primary_assignee}
		<div class="primary-chip" title="Agente responsable">
			<div class="mini-avatar">{conversation.primary_assignee.name?.charAt(0) || '?'}</div>
			<span>{conversation.primary_assignee.name}</span>
			{#if conversation.primary_assignee.presence_status}
				<span
					class="presence"
					style="background:{({online:'var(--success)',busy:'var(--danger)',away:'var(--warning)'})[conversation.primary_assignee.presence_status] || 'var(--text-3)'}"
					title={conversation.primary_assignee.presence_status}
				></span>
			{/if}
		</div>
	{:else}
		<button class="claim-btn" onclick={claim} disabled={busy} type="button">
			<Icon name="user" size={13} />
			Reclamar
		</button>
	{/if}

	{#if conversation.ai_paused}
		<button class="ai-chip paused" onclick={toggleAI} disabled={busy} type="button" title="Reanudar IA">
			<Icon name="bot" size={12} />
			<span>IA pausada — reanudar</span>
		</button>
	{:else}
		<span class="ai-chip" title="Estado IA">
			<Icon name="bot" size={12} />
			IA activa
		</span>
	{/if}

	<div class="transfer-wrap">
		<button class="transfer-btn" onclick={() => { menuOpen = !menuOpen; if (menuOpen) ensureRoster(); }} type="button" disabled={busy}>
			<Icon name="arrow-right" size={13} />
			Transferir
			<Icon name="chevron-down" size={12} />
		</button>
		{#if menuOpen}
			<div class="transfer-menu" role="menu">
				{#if teams.length}
					<div class="menu-section-title">Equipos</div>
					{#each teams as t}
						<button class="menu-item" onmousedown={() => transferTo({ to_team_id: t.id, reason: 'manual' })}>
							<span class="dot" style="background:{t.color || 'var(--accent)'}"></span>
							<span>{t.name}</span>
							<span class="muted">{t.member_count}</span>
						</button>
					{/each}
				{/if}
				{#if roster.length}
					<div class="menu-section-title">Agentes</div>
					{#each roster as u}
						{#if u.id !== conversation.primary_assignee_id}
							<button class="menu-item" onmousedown={() => transferTo({ to_user_id: u.id, reason: 'manual' })}>
								<span class="presence" style="background:{({online:'var(--success)',busy:'var(--danger)',away:'var(--warning)'})[u.presence_status] || 'var(--text-3)'}"></span>
								<span>{u.name}</span>
								<span class="muted">{u.open_conversations ?? 0}/{u.max_concurrent_conversations || 10}</span>
							</button>
						{/if}
					{/each}
				{/if}
				{#if !roster.length && !teams.length && rosterLoaded}
					<div class="empty-menu">Sin agentes ni equipos</div>
				{/if}
			</div>
		{/if}
	</div>

	{#if lead}
		<div class="lead-panel">
			<span class="lead-title" title={lead.title}>
				<Icon name="kanban" size={12} /> {lead.title}
			</span>
			{#if lead.status === 'active'}
				{#if stages.length}
					<select class="lead-stage" value={lead.stage_id} onchange={(e) => moveStage(e.target.value)} disabled={busy}>
						{#each stages as s}
							<option value={s.id}>{s.name}</option>
						{/each}
					</select>
				{/if}
				<button class="lead-won" onclick={() => setLeadStatus('won')} disabled={busy} type="button">Ganado</button>
				<button class="lead-lost" onclick={() => setLeadStatus('lost')} disabled={busy} type="button">Perdido</button>
			{:else}
				<span class="lead-status {lead.status}">{lead.status === 'won' ? '🏆 Ganado' : '✖ Perdido'}</span>
				<button class="lead-reopen" onclick={() => setLeadStatus('active')} disabled={busy} type="button">Reabrir</button>
			{/if}
		</div>
	{:else}
		<button class="lead-btn" onclick={createLead} disabled={creatingLead} type="button">
			<Icon name="kanban" size={13} />
			{creatingLead ? 'Creando...' : 'Crear lead'}
		</button>
	{/if}
</div>

<style>
	.collab-bar {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px;
		padding: 8px 16px;
		background: var(--surface-1);
		border-bottom: 1px solid var(--border-1);
		font-size: 12.5px;
	}
	.primary-chip, .claim-btn, .ai-chip, .transfer-btn {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		padding: 5px 10px;
		border-radius: 9999px;
		background: var(--surface-0);
		border: 1px solid var(--border-1);
		font-size: 12px;
		font-weight: 600;
		color: var(--text-2);
		transition: background var(--transition-fast), border-color var(--transition-fast);
		white-space: nowrap;
	}
	.claim-btn {
		background: var(--accent);
		color: #fff;
		border-color: var(--accent);
		cursor: pointer;
	}
	.claim-btn:hover { background: var(--accent-hover); }
	.transfer-btn { cursor: pointer; }
	.transfer-btn:hover { border-color: var(--text-3); }

	.ai-chip { background: var(--success-soft); color: var(--success); border-color: var(--success-soft); }
	.ai-chip.paused {
		background: var(--warning-soft);
		color: #b45309;
		border-color: var(--warning-soft);
		cursor: pointer;
	}
	.ai-chip.paused:hover { filter: brightness(0.96); }

	.mini-avatar {
		width: 18px; height: 18px;
		border-radius: 50%;
		background: linear-gradient(135deg, var(--brand), var(--brand-dark));
		color: #fff;
		display: flex; align-items: center; justify-content: center;
		font-size: 10px;
		font-weight: 700;
	}
	.presence {
		width: 7px; height: 7px;
		border-radius: 50%;
		display: inline-block;
		flex-shrink: 0;
	}

	.transfer-wrap { position: relative; }
	.transfer-menu {
		position: absolute;
		top: calc(100% + 4px); left: 0;
		min-width: 240px;
		max-height: 360px;
		overflow-y: auto;
		background: var(--surface-0);
		border: 1px solid var(--border-1);
		border-radius: 8px;
		box-shadow: var(--shadow-lg);
		padding: 4px;
		z-index: 30;
	}
	.menu-section-title {
		padding: 6px 10px 4px;
		font-size: 10px;
		font-weight: 700;
		color: var(--text-3);
		text-transform: uppercase;
		letter-spacing: 0.05em;
	}
	.menu-item {
		display: flex; align-items: center; gap: 8px;
		width: 100%;
		padding: 6px 10px;
		font-size: 12.5px;
		color: var(--text-1);
		border-radius: 6px;
		text-align: left;
		transition: background var(--transition-fast);
	}
	.menu-item:hover { background: var(--surface-1); }
	.menu-item .muted { margin-left: auto; color: var(--text-3); font-size: 11px; }
	.dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
	.empty-menu { padding: 14px; text-align: center; color: var(--text-3); font-size: 12px; }

	.lead-btn {
		display: inline-flex; align-items: center; gap: 6px;
		padding: 5px 10px;
		border-radius: 9999px;
		background: var(--brand-soft);
		border: 1px solid var(--brand-soft);
		font-size: 12px; font-weight: 600;
		color: var(--brand-dark);
		cursor: pointer;
		margin-left: auto;
		transition: filter var(--transition-fast);
	}
	.lead-btn:hover { filter: brightness(0.96); }
	.lead-btn:disabled { opacity: 0.6; cursor: not-allowed; }

	.lead-panel {
		display: inline-flex; align-items: center; gap: 6px;
		margin-left: auto;
		padding: 3px 8px;
		border-radius: 9999px;
		background: var(--brand-soft);
		border: 1px solid var(--brand-soft);
		max-width: 60%;
	}
	.lead-title {
		display: inline-flex; align-items: center; gap: 4px;
		font-size: 12px; font-weight: 600; color: var(--brand-dark);
		max-width: 140px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
	}
	.lead-stage {
		font-size: 11.5px; padding: 2px 6px;
		border: 1px solid var(--border-1); border-radius: 6px;
		background: var(--surface-0); cursor: pointer; max-width: 130px;
	}
	.lead-won, .lead-lost, .lead-reopen {
		font-size: 11.5px; font-weight: 600;
		padding: 3px 9px; border-radius: 9999px; cursor: pointer;
		border: 1px solid transparent;
	}
	.lead-won { background: var(--success-soft); color: var(--success); }
	.lead-lost { background: var(--danger-soft, #fde8e8); color: var(--danger, #c0392b); }
	.lead-reopen { background: var(--surface-0); color: var(--text-2); border-color: var(--border-1); }
	.lead-won:disabled, .lead-lost:disabled, .lead-reopen:disabled { opacity: 0.6; cursor: not-allowed; }
	.lead-status { font-size: 12px; font-weight: 700; }
	.lead-status.won { color: var(--success); }
	.lead-status.lost { color: var(--danger, #c0392b); }

	/* Mobile: one tidy scrollable row instead of stacking and pushing the chat down */
	@media (max-width: 768px) {
		.collab-bar {
			flex-wrap: nowrap;
			overflow-x: auto;
			padding: 6px 10px;
			gap: 6px;
			-webkit-overflow-scrolling: touch;
			scrollbar-width: none;
		}
		.collab-bar::-webkit-scrollbar { display: none; }
		.primary-chip, .claim-btn, .ai-chip, .transfer-btn, .lead-btn, .lead-panel {
			flex-shrink: 0;
		}
		.lead-panel { margin-left: 0; max-width: none; }
		.lead-btn { margin-left: 0; }
	}
</style>
